import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Extends Angular's default reuse check (same route config) with a requirement that
 * every inherited route param also matches. Without this, navigating between two
 * matches of the *same* leaf route config that differ only in an ancestor's :id --
 * e.g. tapping a different building on the map while its interaction panel is still
 * open -- would reuse the component instance and skip ngOnInit, leaving the previous
 * address's data on screen. Every page in this app reads route params via a one-time
 * ngOnInit snapshot read, never a reactive paramMap subscription, so reuse must be
 * gated on params too, not just route config identity.
 */
@Injectable({ providedIn: 'root' })
export class ParamAwareReuseStrategy implements RouteReuseStrategy {
  shouldDetach(): boolean {
    return false;
  }

  store(): void {
    // no-op: this app never detaches/re-attaches routes
  }

  shouldAttach(): boolean {
    return false;
  }

  retrieve(): DetachedRouteHandle | null {
    return null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;
    return this.paramsMatch(future, curr);
  }

  private paramsMatch(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const futureKeys = future.paramMap.keys;
    const currKeys = curr.paramMap.keys;
    if (futureKeys.length !== currKeys.length) return false;
    return futureKeys.every((key) => future.paramMap.get(key) === curr.paramMap.get(key));
  }
}
